<template>
  <v-dialog v-model="dialog" max-width="900" scrollable @after-leave="reset">
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Evaluation of {{ student && student.name }}</span>
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" @click="close" />
      </v-card-title>
      <v-divider />

      <v-card-text style="max-height: 700px">
        <div v-if="loading" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate color="#00A63E" />
        </div>

        <v-alert v-else-if="error" type="error" variant="tonal">
          {{ error }}
        </v-alert>

        <div v-else-if="evalData">
          <div class="d-flex align-center ga-2 mb-4">
            <v-chip v-if="finalScore !== null" color="#00A63E" variant="flat">
              Final Score: {{ finalScore }}
            </v-chip>
            <v-chip variant="tonal" size="small">
              {{ student && student.program_name }}
            </v-chip>
          </div>

          <div v-for="sec in sections" :key="sec.letter" class="mb-5">
            <div class="text-subtitle-1 font-weight-bold mb-2">
              {{ sec.title }}
            </div>

            <div
              v-for="item in sec.items"
              :key="item.key"
              class="d-flex justify-space-between align-start py-2 item-row"
            >
              <div class="pr-4" style="flex: 1">{{ item.question }}</div>

              <!-- Rating answers: read-only scale with the chosen value highlighted -->
              <div v-if="isRating(item.evalAns)" class="d-flex ga-2">
                <div
                  v-for="n in scale"
                  :key="n"
                  class="d-flex justify-center align-center border border-1 radio"
                  :class="String(item.evalAns) === String(n) ? 'bg-green' : ''"
                >
                  {{ n }}
                </div>
              </div>

              <!-- Yes/No or free-text answers -->
              <div v-else class="text-right answer-text">
                <v-chip
                  v-if="['Yes', 'No'].includes(item.evalAns)"
                  size="small"
                  :color="item.evalAns === 'Yes' ? 'success' : 'error'"
                  variant="tonal"
                >
                  {{ item.evalAns }}
                </v-chip>
                <span v-else>{{ item.evalAns || "—" }}</span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-2 mr-4">
        <v-spacer />
        <v-btn variant="tonal" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const IT_TITLES = {
  a: "A. PROFESSIONAL ATTITUDE AND WORK ETHICS",
  b: "B. COMMUNICATION AND INTERPERSONAL SKILLS",
  c: "C. PRODUCTIVITY AND WORK EFFICIENCY",
  d: "D. TECHNICAL COMPETENCE AND TASK PERFORMANCE",
  e: "E. BSIS-SPECIFIC TECHNICAL COMPETENCIES",
  f: "F. BSIT-SPECIFIC TECHNICAL COMPETENCIES",
};

const BPA_TITLES = {
  a: "1. Job Knowledge",
  b: "2. Quality of Work",
  c: "3. Quantity of Work",
  d: "4. Dependability",
  e: "5. Creativity",
  f: "6. Organization",
  g: "7. Initiative",
  h: "8. Industry",
  i: "9. Attitude Toward the Job",
  j: "10. Attitude Toward the Work",
  k: "11. Cooperation",
  l: "12. Personality",
  m: "13. Job Assignment",
  n: "14. Overall Qualities",
};

export default {
  props: {
    modelValue: Boolean,
    student: Object, // the intern card item (needs id, name, program_id, program_name)
  },
  emits: ["update:modelValue"],
  data: () => ({
    loading: false,
    error: null,
    evalData: null,
  }),
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
    isBPA() {
      return String(this.student?.program_id) === "5";
    },
    scale() {
      return this.isBPA ? [5, 4, 3, 2, 1] : [5, 4, 3, 2, 1, "NA"];
    },
    sections() {
      if (!this.evalData) return [];
      const titles = this.isBPA ? BPA_TITLES : IT_TITLES;
      const groups = {};

      Object.keys(this.evalData)
        .filter((k) => /^[a-z]_\d+$/.test(k))
        .sort((x, y) => {
          const [lx, nx] = x.split("_");
          const [ly, ny] = y.split("_");
          return lx === ly ? Number(nx) - Number(ny) : lx.localeCompare(ly);
        })
        .forEach((key) => {
          const letter = key.split("_")[0];
          (groups[letter] = groups[letter] || []).push({
            key,
            question: this.evalData[key].question,
            evalAns: this.evalData[key].evalAns,
          });
        });

      return Object.keys(groups)
        .sort()
        .map((letter) => ({
          letter,
          title: titles[letter] || letter.toUpperCase(),
          items: groups[letter],
        }));
    },
    // Same formula as the report: ((score - deduction) / total) * 50 + 50, NA excluded
    finalScore() {
      if (!this.evalData) return null;
      let score = 0;
      let max = 0;
      Object.values(this.evalData).forEach((v) => {
        if (v && this.isRating(v.evalAns) && String(v.evalAns) !== "NA") {
          score += Number(v.evalAns);
          max += 5;
        }
      });
      return max > 0 ? Math.round((score / max) * 50 + 50) : null;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },
  methods: {
    isRating(ans) {
      return ["1", "2", "3", "4", "5", "NA"].includes(String(ans));
    },
    load() {
      if (!this.student?.id) return;
      this.loading = true;
      this.error = null;
      this.evalData = null;

      // NOTE: adjust this endpoint to match your backend
      this.axiosCall(
        "/evaluation-util/student-evaluation/" + this.student.id,
        "GET",
      )
        .then((res) => {
          let payload = res?.data?.evaluation ?? res?.data;
          if (typeof payload === "string") payload = JSON.parse(payload);
          if (!payload || !Object.keys(payload).length) {
            this.error = "No evaluation found for this student.";
          } else {
            this.evalData = payload;
          }
        })
        .catch(() => {
          this.error = "Failed to load the evaluation.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reset() {
      this.evalData = null;
      this.error = null;
    },
    close() {
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.item-row {
  border-bottom: 1px solid #e5e7eb;
}
.radio {
  width: 24px;
  height: 24px;
  font-size: 10px;
  border-radius: 100%;
}
.answer-text {
  max-width: 50%;
  white-space: pre-wrap;
}
</style>
